/**
 * Browser auth session metadata.
 *
 * The actual DRF token is held in an httpOnly cookie set by the API. This
 * class keeps only non-secret expiry/user hints for UI gating and removes
 * legacy localStorage bearer tokens when encountered.
 */
export class TokenManager {
  private static readonly TOKEN_KEY = 'auth_token';
  private static readonly EXPIRES_KEY = 'auth_expires_at';
  private static readonly REFRESH_KEY = 'refresh_token';
  private static readonly USER_KEY = 'user_data';

  private static get storage(): Storage | null {
    try {
      return window.sessionStorage;
    } catch {
      return null;
    }
  }

  private static removeLegacyTokens(): void {
    try {
      localStorage.removeItem(this.TOKEN_KEY);
      localStorage.removeItem(this.REFRESH_KEY);
      localStorage.removeItem('access_token');
    } catch {
      // Best-effort cleanup only.
    }
  }

  static setTokens(data: {
    token?: string;
    refreshToken?: string;
    expiresAt: string;
    user: any;
  }): void {
    const storage = this.storage;
    if (!storage) return;

    const expirationDate = new Date(data.expiresAt);
    const expiresAt = isNaN(expirationDate.getTime())
      ? new Date(Date.now() + 60 * 60 * 1000).toISOString()
      : data.expiresAt;

    storage.setItem(this.EXPIRES_KEY, expiresAt);
    if (data.user) {
      storage.setItem(this.USER_KEY, JSON.stringify(data.user));
    } else {
      storage.removeItem(this.USER_KEY);
    }
    this.removeLegacyTokens();
  }

  static getToken(): string | null {
    this.removeLegacyTokens();
    return null;
  }

  static isTokenValid(): boolean {
    const expiresAt = this.storage?.getItem(this.EXPIRES_KEY);
    if (!expiresAt) return false;

    try {
      return new Date(expiresAt).getTime() > Date.now();
    } catch {
      return false;
    }
  }

  static isTokenExpiring(threshold: number = 5 * 60 * 1000): boolean {
    const expiresAt = this.storage?.getItem(this.EXPIRES_KEY);
    if (!expiresAt) return true;

    try {
      return new Date(expiresAt).getTime() - Date.now() < threshold;
    } catch {
      return true;
    }
  }

  static getRefreshToken(): string | null {
    this.removeLegacyTokens();
    return null;
  }

  static clearTokens(): void {
    try {
      this.storage?.removeItem(this.EXPIRES_KEY);
      this.storage?.removeItem(this.USER_KEY);
      this.removeLegacyTokens();
      localStorage.removeItem(this.EXPIRES_KEY);
      localStorage.removeItem(this.USER_KEY);
    } catch (error) {
      console.error('Failed to clear auth session:', error);
    }
  }

  static getUser(): any | null {
    try {
      const userData = this.storage?.getItem(this.USER_KEY);
      if (!userData || userData === 'undefined' || userData === 'null' || userData.trim() === '') {
        return null;
      }

      const parsedUser = JSON.parse(userData);
      if (!parsedUser || typeof parsedUser !== 'object' || !parsedUser.id) {
        this.storage?.removeItem(this.USER_KEY);
        return null;
      }

      return parsedUser;
    } catch {
      this.storage?.removeItem(this.USER_KEY);
      return null;
    }
  }

  static updateToken(_newToken?: string, ttlMs: number = 60 * 60 * 1000): void {
    this.storage?.setItem(this.EXPIRES_KEY, new Date(Date.now() + ttlMs).toISOString());
    this.removeLegacyTokens();
  }

  static updateUser(user: any): void {
    const storage = this.storage;
    if (!storage) return;

    storage.setItem(this.USER_KEY, JSON.stringify(user));
    this.removeLegacyTokens();
  }
}
